import React, { useState, useEffect } from "react";
import AdminAppBody from "../../../../components/admin/AdminAppBody";
import PageHeading from "../../../../components/admin/common/PageHeading";
import useApiCallHooks from "../../../../hooks/useApiCallHooks";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { uiRoutes } from "../../../../routes/ui/uiRoutes";

const RoboticsKitAdd = () => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    grades: "",
    age: "",
    image: null,
    level: "",
    projects: [{ name: "", file: null }],
    what_you_learn: [""],
    price: "",
  });

  const [imagePreview, setImagePreview] = useState(null);
  const navigate = useNavigate();
  const [response, loading, error, callAPI, statusCode] = useApiCallHooks();

  useEffect(() => {
    if (response?.data && statusCode === 201) {
      toast.success("Robotics Kit added successfully!");
      setTimeout(() => {
        navigate(uiRoutes.admin.roboticskit.list);
      }, 1000);
    }
  }, [response, statusCode, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (key === "projects") {
        value.forEach((project, index) => {
          submitData.append(`projects[${index}][name]`, project.name);
          if (project.file) {
            submitData.append(`projects[${index}][file]`, project.file);
          }
        });
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          submitData.append(`${key}[${index}]`, item);
        });
      } else {
        submitData.append(key, value);
      }
    });

    callAPI("post", "admin/robotic-kits", submitData);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, image: file });
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const handleProjectChange = (index, field, value) => {
    const updated = [...formData.projects];
    updated[index][field] = value;
    setFormData({ ...formData, projects: updated });
  };

  const addProject = () => {
    setFormData({ ...formData, projects: [...formData.projects, { name: "", file: null }] });
  };

  const removeProject = (index) => {
    const updated = [...formData.projects];
    updated.splice(index, 1);
    setFormData({ ...formData, projects: updated });
  };

  const handleArrayChange = (field, index, value) => {
    const updated = [...formData[field]];
    updated[index] = value;
    setFormData({ ...formData, [field]: updated });
  };

  const addField = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ""] });
  };

  const removeField = (field, index) => {
    const updated = [...formData[field]];
    updated.splice(index, 1);
    setFormData({ ...formData, [field]: updated });
  };

  return (
    <AdminAppBody
      loading={loading}
      content={
        <div className="container mt-5">
          <PageHeading title="Add Robotics Kit" subTitle="Add kit details" />
          <div className="container card p-4 shadow-sm mt-3">
            <form onSubmit={handleSubmit} className="row g-3">
              <h6>Add Robotics Kit Form</h6>

              <div className="col-md-6">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                <span className="text-danger">
                  {error?.message === "ValidationError" && error?.data?.name}
                </span>
              </div>

              <div className="col-md-6">
                <label className="form-label">Grades (e.g., 1-2)</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.grades}
                  onChange={(e) => setFormData({ ...formData, grades: e.target.value })}
                />
                <span className="text-danger">
                  {error?.message === "ValidationError" && error?.data?.grades}
                </span>
              </div>

              <div className="col-md-6">
                <label className="form-label">Age Group (e.g., 12-13)</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                />
                <span className="text-danger">
                  {error?.message === "ValidationError" && error?.data?.age}
                </span>
              </div>

              <div className="col-md-6">
                <label className="form-label">Level</label>
                <select
                  className="form-select"
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                >
                  <option value="">Select Level</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
                <span className="text-danger">
                  {error?.message === "ValidationError" && error?.data?.level}
                </span>
              </div>

              <div className="col-md-6">
                <label className="form-label">Image Upload</label>
                <input
                  type="file"
                  accept="image/*"
                  className="form-control"
                  onChange={handleImageChange}
                />
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="mt-2"
                    style={{ width: "100%", maxHeight: "200px", objectFit: "contain" }}
                  />
                )}
                <span className="text-danger">
                  {error?.message === "ValidationError" && error?.data?.image}
                </span>
              </div>

              <div className="col-md-6">
                <label className="form-label">Price (₹)</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                />
                <span className="text-danger">
                  {error?.message === "ValidationError" && error?.data?.price}
                </span>
              </div>

              <div className="col-md-6">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
                <span className="text-danger">
                  {error?.message === "ValidationError" && error?.data?.description}
                </span>
              </div>

              <div className="col-md-6">
                <label className="form-label">Projects</label>
                {formData.projects.map((project, index) => (
                  <div key={index} className="mb-2">
                    <input
                      type="text"
                      className="form-control mb-1"
                      placeholder="Project Name"
                      value={project.name}
                      onChange={(e) => handleProjectChange(index, "name", e.target.value)}
                    />
                    <input
                      type="file"
                      accept=".pdf,.doc,.zip"
                      className="form-control mb-1"
                      onChange={(e) => handleProjectChange(index, "file", e.target.files[0])}
                    />
                    <div className="d-flex justify-content-end">
                      {formData.projects.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => removeProject(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={addProject}
                >
                  + Add Project
                </button>
              </div>

              <div className="col-md-6">
                <label className="form-label">What You Learn</label>
                {formData.what_you_learn.map((item, index) => (
                  <div key={index} className="d-flex gap-2 mb-2">
                    <input
                      type="text"
                      className="form-control"
                      value={item}
                      onChange={(e) => handleArrayChange("what_you_learn", index, e.target.value)}
                    />
                    {formData.what_you_learn.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => removeField("what_you_learn", index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => addField("what_you_learn")}
                >
                  + Add Learning Point
                </button>
              </div>

              <div className="col-md-4 mt-4 mb-4">
                <button type="submit" className="btn btn-primary btn-sm w-100">
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      }
    />
  );
};

export default RoboticsKitAdd;
